import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Trans } from 'react-i18next';

import { Button, Input, WebCopy, WebHeading, Loader } from 'components/shared';
import Icon from 'assets/icons';

import { useTranslation } from 'hooks';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import { OrganizationTypes, appEnvironment } from 'helpers';
import { isChrome, isExtensionInstalled } from 'helpers/extension';
import { trackingOnboardingAddYourTeamView } from 'services';

import styles from './AddYourTeam.module.scss';
import obstyles from '../../OnboardingScreen.module.scss';

interface AddYourTeamData {
  me: {
    organization: Organization;
  };
}

const getOrganizationQuery = gql`
  query getIntegrations {
    me {
      organization {
        id
        name
        type
        signupDomain
        url
      }
    }
  }
`;

const AddYourTeam = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const advanceOnboarding = React.useCallback(() => {
    /**
     * It should only go to the add-to-chrome screen if:
     * User is using Chrome
     * User doesn't have the extension installed
     * User is using the web version
     */
    if (isChrome && !isExtensionInstalled && appEnvironment === 'web') {
      history.push('/onboarding/add-to-chrome');
    } else {
      history.push('/onboarding/finish');
    }
  }, [history]);

  const { error, data } = useQuery<AddYourTeamData>(getOrganizationQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const redirectUser = data?.me.organization.type === OrganizationTypes.PERSONAL;

  useEffect(() => {
    if (!redirectUser) {
      trackingOnboardingAddYourTeamView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  if (redirectUser) {
    advanceOnboarding();
  }

  return (
    <div className={obstyles.contentLayout}>
      <div className={styles.contentWrapper}>
        <WebHeading className={styles.title} level={1}>
          {t('onboarding.add-team.heading', { teamName: data.me.organization.name })}
        </WebHeading>

        <WebCopy className={styles.copy}>{t('onboarding.add-team.subheading')}</WebCopy>
      </div>

      <div className={styles.addTeamWrapper}>
        <div className={styles.shareSteps}>
          <div className={styles.upper}>
            <div className={styles.heading}>{t('onboarding.add-team.step1')}</div>
            <Input copyOnly className={styles.textInput} value={data.me.organization.url} />
          </div>

          <div className={styles.lower}>
            <div className={styles.heading}>{t('onboarding.add-team.step2')}</div>
            <div className={styles.addTeamCopy}>
               
              <Trans
                i18nKey="onboarding.add-team.step2-text"
                values={{ teamName: data.me.organization.name }}
              />
            </div>
          </div>
        </div>
        <div className={styles.info}>
          {t('onboarding.add-team.info', { signupDomain: data.me.organization.signupDomain })}
        </div>
      </div>

      <div className={obstyles.footer}>
        <Button className={styles.next} size="Large" onClick={advanceOnboarding}>
          {t('onboarding.continue')}
          <Icon.ChevronThick textPosition="Right" />
        </Button>
      </div>
    </div>
  );
};

export default AddYourTeam;
